import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loginUserThunk, connectWithAlternativeSsoThunk } from 'actions/authActions';
import { checkUserThunk, logoutUserThunk, refreshUserThunk } from 'actions/userActions';
import { IS_AUTHORIZED_LS_KEY } from 'modules/auth/constants/auth.const';
import { EMPTY_STRING } from 'modules/core/constants';
import { ILenderSettingsData, IUserPayload } from 'modules/core/types';

export type AuthReducerState = {
  session_role: string;
  isFetching: boolean;
  isAuth: boolean;
  isError: boolean;
  isAlternativeSsoFetching: boolean;
  isAlternativeSsoError: boolean;
  errorMessage: string;
  user: IUserPayload | null;
};

const initialState: AuthReducerState = {
  session_role: 'manager',
  isFetching: true,
  isAuth: false,
  isError: false,
  isAlternativeSsoFetching: true,
  isAlternativeSsoError: false,
  errorMessage: EMPTY_STRING,
  user: null,
};

export const authSlice = createSlice({
  initialState,
  name: 'auth',
  reducers: {
    clearState: () => ({ ...initialState }),
    setUserLenderSettings: (state: AuthReducerState, { payload }: PayloadAction<ILenderSettingsData>) => {
      if (!state.user) return state;

      return {
        ...state,
        user: {
          ...state.user,
          lender_settings_data: payload,
        },
      };
    },
  },
  extraReducers: {
    [loginUserThunk.fulfilled.type]: (state) => ({
      ...state,
      isFetching: false,
      isAuth: true,
      isError: false,
    }),
    [loginUserThunk.pending.type]: (state: AuthReducerState) => ({ ...state, isFetching: true }),
    [loginUserThunk.rejected.type]: (state, { payload: { detail } }) => ({
      ...state,
      errorMessage: detail,
      isFetching: false,
      isError: true,
    }),
    [checkUserThunk.pending.type]: (state: AuthReducerState) => ({ ...state, isFetching: true }),
    [checkUserThunk.fulfilled.type]: (state: AuthReducerState, { payload: user }) => ({
      ...state,
      user,
      isFetching: false,
      isAuth: !!localStorage.getItem(IS_AUTHORIZED_LS_KEY),
    }),
    [checkUserThunk.rejected.type]: (state: AuthReducerState) => ({ ...state, isFetching: false }),
    [refreshUserThunk.fulfilled.type]: (state: AuthReducerState, { payload: user }) => ({ ...state, user }),
    [refreshUserThunk.rejected.type]: (state: AuthReducerState) => ({ ...state }),
    [connectWithAlternativeSsoThunk.pending.type]: (state: AuthReducerState) => ({
      ...state,
      isAlternativeSsoFetching: true,
      isAlternativeSsoError: false,
    }),
    [connectWithAlternativeSsoThunk.fulfilled.type]: (state: AuthReducerState) => ({
      ...state,
      isAlternativeSsoFetching: false,
    }),
    [connectWithAlternativeSsoThunk.rejected.type]: (state: AuthReducerState) => ({
      ...state,
      isAlternativeSsoFetching: false,
      isAlternativeSsoError: true,
    }),
    [logoutUserThunk.pending.type]: (state: AuthReducerState) => ({ ...state, isFetching: true }),
    [logoutUserThunk.fulfilled.type]: (state: AuthReducerState) => ({ ...state, isFetching: false }),
    [logoutUserThunk.rejected.type]: (state: AuthReducerState) => ({ ...state, isFetching: false }),
  },
});

export const { clearState, setUserLenderSettings } = authSlice.actions;
